import React, { useEffect, useState } from "react";
import Table from "../../extra/Table";
import { connect, useDispatch, useSelector } from "react-redux";
import {
  getServiceProviderRequest,
  acceptServiceProviderRequest,
  rejectServiceProviderRequest,
} from "../../store/serviceProviderRequest/serviceProviderRequest.action";
import dayjs from "dayjs";
import Pagination from "../../extra/Pagination";
import { thumb } from "../../../util/thumb";
import Searching from "../../extra/Searching";
import { warningAccept } from "../../../util/Alert";
import { apiInstanceFetch } from "../../../util/api";
import defaultImage from "../../../assets/images/default.jpg";
import ServiceProviderDocumentsDialog from "./ServiceProviderDocumentsDialog";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button as MuiButton,
} from "@mui/material";

const ServiceProviderRequest = (props) => {
  const [page, setPage] = useState(0);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(25); // boss 2026-09-30: 25/50/100/500
  const [statusFilter, setStatusFilter] = useState("pending");

  // Document preview dialog
  const [docsOpen, setDocsOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Reject reason dialog
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const dispatch = useDispatch();
  const { serviceProviderRequest } = useSelector(
    (state) => state.serviceProviderRequest
  );

  useEffect(() => {
    dispatch(getServiceProviderRequest(statusFilter));
  }, [dispatch, statusFilter]);

  useEffect(() => {
    setData(serviceProviderRequest);
  }, [serviceProviderRequest]);

  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event, 10));
    setPage(0);
  };

  const handleApprove = (id) => {
    warningAccept()
      .then((ok) => {
        if (ok) dispatch(acceptServiceProviderRequest(id));
      })
      .catch((err) => console.log(err));
  };

  const openRejectDialog = (id) => {
    setRejectId(id);
    setRejectReason("");
    setRejectOpen(true);
  };

  const submitReject = () => {
    if (!rejectReason.trim()) return;
    dispatch(rejectServiceProviderRequest(rejectId, rejectReason.trim()));
    setRejectOpen(false);
    setRejectId(null);
    setRejectReason("");
  };

  const handleViewDocs = (row) => {
    setSelectedRow(row);
    setDocsOpen(true);
  };

  const handleFilterData = (filtered) => {
    if (typeof filtered === "string") setSearch(filtered);
    else setData(filtered);
  };

  // Hard delete — removes the request and, if it was approved, revokes
  // provider status + profile so the person leaves the marketplace.
  const handleDelete = async (row) => {
    const name = `${row?.firstName || ""} ${row?.lastName || ""}`.trim() || row?.providerName || "this provider";
    if (!window.confirm(`Delete ${name}? This removes the request${row.isAccepted ? " AND revokes their provider access" : ""}. This cannot be undone.`)) return;
    try {
      const res = await apiInstanceFetch.delete(`serviceProviderRequest/${row._id}`);
      if (res?.status) {
        dispatch(getServiceProviderRequest(statusFilter));
      } else {
        alert(res?.message || "Delete failed.");
      }
    } catch (e) {
      console.error("serviceProviderRequest delete error", e);
      alert("Delete failed.");
    }
  };

  const mapData = [
    {
      Header: "No",
      width: "20px",
      Cell: ({ index }) => (
        <span className="text-white fw-normal">
          {page * rowsPerPage + parseInt(index) + 1}
        </span>
      ),
    },
    {
      Header: "Logo",
      Cell: ({ row }) => (
        <img
          src={thumb(row.logo || row.userImage) || defaultImage}
          loading="lazy"
          decoding="async"
          height={45}
          width={45}
          style={{ borderRadius: "50px", objectFit: "cover" }}
          alt="provider logo"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = defaultImage;
          }}
        />
      ),
    },
    {
      Header: "User",
      Cell: ({ row }) => (
        <span className="text-white fw-normal text-capitalize">
          {(row?.firstName || "") + " " + (row?.lastName || "")}
        </span>
      ),
    },
    {
      Header: "Provider Name",
      Cell: ({ row }) => (
        <span className="text-white">{row?.providerName || "-"}</span>
      ),
    },
    {
      Header: "Category",
      Cell: ({ row }) => (
        <span className="text-white">{row?.serviceCategory || "-"}</span>
      ),
    },
    {
      Header: "Country",
      Cell: ({ row }) => (
        <span className="text-white">{row?.address?.country || "-"}</span>
      ),
    },
    {
      Header: "Documents",
      Cell: ({ row }) => {
        const has = row?.govId || row?.addressProof || row?.logo;
        return (
          <button
            onClick={() => handleViewDocs(row)}
            style={{
              padding: "8px 12px",
              backgroundColor: has ? "#F0C420" : "#444",
              border: "none",
              borderRadius: "6px",
              color: has ? "#000" : "#888",
              fontWeight: "500",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <DescriptionOutlinedIcon style={{ fontSize: "18px" }} />
            View
          </button>
        );
      },
    },
    {
      Header: "Action",
      Cell: ({ row }) => {
        const deleteBtn = (
          <button
            onClick={() => handleDelete(row)}
            style={{
              padding: "6px 10px",
              backgroundColor: "#7f1d1d",
              border: "none",
              borderRadius: "6px",
              color: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <HighlightOffIcon style={{ fontSize: "18px" }} /> Delete
          </button>
        );
        if (statusFilter !== "pending") {
          return (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <span className="text-white">
                {row.isAccepted
                  ? "Approved"
                  : row.rejectionReason
                  ? `Rejected: ${row.rejectionReason}`
                  : "—"}
              </span>
              {deleteBtn}
            </div>
          );
        }
        return (
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => handleApprove(row._id)}
              style={{
                padding: "6px 10px",
                backgroundColor: "#16a34a",
                border: "none",
                borderRadius: "6px",
                color: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <CheckCircleOutlineIcon style={{ fontSize: "18px" }} /> Approve
            </button>
            <button
              onClick={() => openRejectDialog(row._id)}
              style={{
                padding: "6px 10px",
                backgroundColor: "#dc2626",
                border: "none",
                borderRadius: "6px",
                color: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <HighlightOffIcon style={{ fontSize: "18px" }} /> Reject
            </button>
            {deleteBtn}
          </div>
        );
      },
    },
    {
      Header: "Submitted",
      Cell: ({ row }) => (
        <span className="text-white">
          {dayjs(row.createdAt).format("DD MMM YYYY")}
        </span>
      ),
    },
  ];

  return (
    <>
      <ServiceProviderDocumentsDialog
        open={docsOpen}
        onClose={() => setDocsOpen(false)}
        providerData={selectedRow}
      />

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject service provider request</DialogTitle>
        <DialogContent>
          <p style={{ marginTop: 0, color: "#6b7280" }}>
            Tell the applicant why their request was rejected. They will see this
            message on the &quot;Become a service provider&quot; screen.
          </p>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setRejectOpen(false)}>Cancel</MuiButton>
          <MuiButton
            onClick={submitReject}
            disabled={!rejectReason.trim()}
            variant="contained"
            color="error"
          >
            Reject
          </MuiButton>
        </DialogActions>
      </Dialog>

      <div className="mainSellerTable">
        <div className="sellerTable">
          <div className="col-12 headname">Service Provider Requests</div>

          <div style={{ display: "flex", gap: "8px", padding: "12px 16px 0 16px" }}>
            {["pending", "approved", "rejected"].map((s) => (
              <button
                key={s}
                onClick={() => {
                  setStatusFilter(s);
                  setPage(0);
                }}
                style={{
                  padding: "6px 14px",
                  border: "1px solid",
                  borderColor: statusFilter === s ? "#F0C420" : "#444",
                  backgroundColor: statusFilter === s ? "#F0C420" : "transparent",
                  color: statusFilter === s ? "#000" : "#1A1614",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: 500,
                  textTransform: "capitalize",
                }}
              >
                {s}
              </button>
            ))}
          </div>

          <div className="sellerMain">
            <div className="tableMain mt-2">
              <div className="sellerHeader primeHeader">
                <div className="row">
                  <div className="col-2"></div>
                  <div className="col-10">
                    <Searching
                      type={"client"}
                      data={serviceProviderRequest}
                      setData={setData}
                      onFilterData={handleFilterData}
                      serverSearching={handleFilterData}
                    />
                  </div>
                </div>
              </div>
              <Table
                data={data}
                mapData={mapData}
                PerPage={rowsPerPage}
                Page={page}
                type={"client"}
              />
              <Pagination
                component="div"
                count={serviceProviderRequest?.length}
                serverPage={page}
                type={"client"}
                onPageChange={handleChangePage}
                serverPerPage={rowsPerPage}
                totalData={serviceProviderRequest?.length}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[25, 50, 100, 500]}
              />
            </div>
          </div>
          <div className="sellerFooter primeFooter"></div>
        </div>
      </div>
    </>
  );
};

export default connect(null, {
  getServiceProviderRequest,
  acceptServiceProviderRequest,
  rejectServiceProviderRequest,
})(ServiceProviderRequest);
